// Agenda: the parts of the consult the owner sees on screen, and which of the
// Durable Object's stages each part covers. The DO moves through stage ids;
// the browser highlights the agenda item that owns the current stage.
import { z } from "zod";
import { DraftLabel, Id, behaviorText, requireUnique } from "./common";

export const AgendaItem = z.object({
  id: Id,
  /** Shown on screen, so short enough for a phone. */
  label: behaviorText(40),
  target_minutes: z.number().positive().max(30),
  /** Stage ids, in the order the consult moves through them. */
  stage_ids: z.array(Id).min(1).max(10),
  /**
   * Optional. A clock note sent to Otto when the clock moves the call into
   * this part, so he knows where he is (Oct 14 2026: with no sense of time he
   * ran out of questions around minute seven and wrapped up on his own).
   * Never sent for the read-back or the close, which have their own notes.
   */
  otto_note: behaviorText(500).optional(),
});
export type AgendaItem = z.infer<typeof AgendaItem>;

/**
 * The 3D Otto's expression cues (design 7.2). Keep in step with AVATAR_MOODS
 * in @otto/protocol (a test in @otto/api checks they match).
 */
export const AGENDA_MOODS = ["neutral", "warm", "sympathetic", "surprised", "delighted"] as const;
export const AgendaMood = z.enum(AGENDA_MOODS);
export type AgendaMood = z.infer<typeof AgendaMood>;

export const Agenda = z
  .object({
    draft_label: DraftLabel,
    items: z.array(AgendaItem).min(1).max(10),
    /**
     * The stage the read-back happens in, and the stage of the close. The
     * Durable Object enters these by events (the read-back is asked for or
     * spoken; the closing beats are asked for), never by time alone.
     * Absent: they are derived as the first and the last of
     * coach.silent_stage_ids in agenda order (the rule before these fields
     * existed). See stageRoles() in bundle.ts.
     */
    readback_stage_id: Id.optional(),
    close_stage_id: Id.optional(),
    /** Optional expression cue per stage id (design 7.2: greeting warm, pains sympathetic, close delighted). */
    stage_moods: z.record(Id, AgendaMood).optional(),
  })
  .superRefine((agenda, ctx) => {
    requireUnique(
      agenda.items.map((i) => i.id),
      ctx,
      ["items"],
      "agenda item id",
    );
    requireUnique(
      agenda.items.flatMap((i) => i.stage_ids),
      ctx,
      ["items"],
      "stage id (a stage belongs to one agenda item)",
    );
    const stages = agenda.items.flatMap((i) => i.stage_ids);
    for (const field of ["readback_stage_id", "close_stage_id"] as const) {
      const id = agenda[field];
      if (id !== undefined && !stages.includes(id)) ctx.addIssue({ code: "custom", path: [field], message: `${id} is not a stage in the agenda` });
    }
    if (agenda.readback_stage_id && agenda.close_stage_id) {
      if (stages.indexOf(agenda.readback_stage_id) >= stages.indexOf(agenda.close_stage_id)) {
        ctx.addIssue({ code: "custom", path: ["close_stage_id"], message: "The close stage comes after the read-back stage" });
      }
    }
    for (const id of Object.keys(agenda.stage_moods ?? {})) {
      if (!stages.includes(id)) ctx.addIssue({ code: "custom", path: ["stage_moods", id], message: `${id} is not a stage in the agenda` });
    }
  });
export type Agenda = z.infer<typeof Agenda>;

/** Every stage id, in consult order. */
export function agendaStageIds(agenda: Agenda): string[] {
  return agenda.items.flatMap((i) => i.stage_ids);
}

/** Index of the agenda item that owns a stage, or -1. */
export function agendaIndexForStage(agenda: Agenda, stageId: string): number {
  return agenda.items.findIndex((i) => i.stage_ids.includes(stageId));
}

export function agendaTotalMinutes(agenda: Agenda): number {
  return agenda.items.reduce((sum, i) => sum + i.target_minutes, 0);
}
